import type { ComboboxItem } from "@mantine/core"

import {
  type UseLazyAutocompleteOptions,
  useLazyAutocomplete,
} from "../../../hooks/useLazyAutocomplete"

import {
  Autocomplete,
  type AutocompleteProps,
} from "./Autocomplete"
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type RefObject,
} from "react"

type LazyAutocompleteHookOptions<
  TItem,
  TOption extends ComboboxItem,
> = Pick<
  UseLazyAutocompleteOptions<TItem, TOption>,
  | "debounceMs"
  | "minChars"
  | "fetchOnFocus"
  | "initialSearchValue"
  | "initialEnabled"
>

export type LazyAutocompleteProps<
  TItem,
  TOption extends ComboboxItem,
> = LazyAutocompleteHookOptions<TItem, TOption> & {
  fetchPage: UseLazyAutocompleteOptions<TItem, TOption>["fetchPage"]
  toOption: (item: TItem) => TOption
  /**
   * Prepended to async `options` before `data` is passed to the combobox.
   * `searchValue` is live input; `remoteOptions` are options from the latest fetch (before prepend).
   */
  prependOptions?: (args: {
    searchValue: string
    remoteOptions: readonly TOption[]
  }) => readonly TOption[]
  /** Ref to the underlying input for imperative blur/focus. */
  inputRef?: RefObject<HTMLInputElement | null>
  /**
   * When applying a remote suggestion outside Mantine `onOptionSubmit`, assign this ref to
   * `(label) => void` so debounced parent sync and visible input match a normal option pick.
   */
  outsideSubmitSyncRef?: RefObject<((label: string) => void) | null>
  /**
   * Fired after the lazy combobox closes on input blur (before `onBlur`), when the user did not
   * pick an option via Mantine (no `onOptionSubmit` for this interaction).
   */
  onComboboxIdleBlur?: (args: {
    searchValue: string
    remoteOptions: readonly TOption[]
  }) => void
  onOptionSelected?: (
    option: TOption,
    context: { readonly remoteOptions: readonly TOption[] }
  ) => void
  onDebouncedSearchValueChange?: (value: string) => void
} & Omit<
    AutocompleteProps,
    | "data"
    | "onChange"
    | "isLoading"
    | "onOptionSubmit"
    | "defaultValue"
    | "value"
  >

export const LazyAutocomplete = <
  TItem,
  TOption extends ComboboxItem
>({
  fetchPage,
  toOption,
  debounceMs = 300,
  minChars,
  fetchOnFocus,
  initialSearchValue,
  initialEnabled,
  onOptionSelected,
  onDebouncedSearchValueChange,
  prependOptions,
  inputRef,
  outsideSubmitSyncRef,
  onComboboxIdleBlur,
  onFocus,
  onBlur,
  onDropdownOpen,
  onDropdownClose,
  filter,
  ...props
}: LazyAutocompleteProps<TItem, TOption>) => {
  // Skip one debounced callback after picking a dropdown option. Mantine sets the input from
  // the option label, so `onChange` / debounced value match `label`, not ComboboxItem `value`.
  const suppressDebouncedSearchValueChange = useRef<string | null>(null)
  /** `setTimeout(0)` defers idle blur past pointer/option selection so Mantine `onOptionSubmit` runs first. */
  const comboboxIdleBlurTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )

  const cancelScheduledComboboxIdleBlur = useCallback((): void => {
    if (comboboxIdleBlurTimeoutId.current !== null) {
      clearTimeout(comboboxIdleBlurTimeoutId.current)
      comboboxIdleBlurTimeoutId.current = null
    }
  }, [])
  const handleDebouncedSearchValueChange = useCallback((value: string) => {
    if (suppressDebouncedSearchValueChange.current === value) {
      suppressDebouncedSearchValueChange.current = null
      return
    }
    onDebouncedSearchValueChange?.(value)
  }, [onDebouncedSearchValueChange])

  const {
    options,
    searchValue,
    setSearchValue,
    onFocus: onLazyFocus,
    onDropdownOpen: onLazyDropdownOpen,
    close: onLazyClose,
    isOpen,
    isLoading,
    isRefreshing,
  } = useLazyAutocomplete<TItem, TOption>({
    fetchPage,
    toOption,
    debounceMs,
    minChars,
    fetchOnFocus,
    initialSearchValue,
    initialEnabled,
    onDebouncedSearchValueChange: handleDebouncedSearchValueChange,
  })

  const mergedOptions = useMemo((): readonly TOption[] => {
    const prepended =
      prependOptions?.({ searchValue, remoteOptions: options }) ?? []
    return [...prepended, ...options]
  }, [options, prependOptions, searchValue])

  useEffect(() => {
    if (outsideSubmitSyncRef === undefined) {
      return undefined
    }
    outsideSubmitSyncRef.current = (label: string): void => {
      suppressDebouncedSearchValueChange.current = label
      setSearchValue(label)
    }
    return () => {
      outsideSubmitSyncRef.current = null
    }
  }, [outsideSubmitSyncRef, setSearchValue])

  useEffect(() => {
    return () => {
      cancelScheduledComboboxIdleBlur()
    }
  }, [cancelScheduledComboboxIdleBlur])

  return (
    <Autocomplete
      {...props}
      ref={inputRef}
      data={mergedOptions}
      // Use a no-op fallback filter for async options, but keep caller override.
      filter={filter ?? (({ options }) => options)}
      value={searchValue}
      isLoading={isOpen && (isLoading || isRefreshing)}
      onChange={(value) => {
        setSearchValue(value)
      }}
      onFocus={(event) => {
        onLazyFocus()
        onFocus?.(event)
      }}
      onBlur={(event) => {
        onLazyClose()
        if (onComboboxIdleBlur) {
          cancelScheduledComboboxIdleBlur()
          const searchSnapshot = searchValue
          const remoteSnapshot = options
          comboboxIdleBlurTimeoutId.current = setTimeout(() => {
            comboboxIdleBlurTimeoutId.current = null
            onComboboxIdleBlur({
              searchValue: searchSnapshot,
              remoteOptions: remoteSnapshot,
            })
          }, 0)
        }
        onBlur?.(event)
      }}
      onDropdownOpen={() => {
        onLazyDropdownOpen()
        onDropdownOpen?.()
      }}
      onDropdownClose={() => {
        onLazyClose()
        onDropdownClose?.()
      }}
      onOptionSubmit={(optionValue) => {
        const selectedOption =
          mergedOptions.find((option) => option.value === optionValue) ?? null
        suppressDebouncedSearchValueChange.current =
          selectedOption?.label ?? optionValue
        if (selectedOption !== null) {
          onOptionSelected?.(selectedOption, { remoteOptions: options })
        }
      }}
    />
  )
}
